import { injectable } from 'inversify';
import { Point } from 'bpmn-js/lib/features/snapping/BpmnSnappingUtil';

import {
  GROUP_PADDING,
  GROUP_PADDING_BOTTOM,
  HALF,
  NODE_HEIGHT,
  NODE_WIDTH,
  PARALLEL_INNER_GAP,
  SYSTEM_NODE_HEIGHT,
} from '../../constants/eventTypes';
import type { FlowNode, FlowSchema, FlowEdge } from '../../types/event-types';

const VERTICAL_GAP = 80;
const SHORT_VERTICAL_GAP = 30;
const HORIZONTAL_GAP = 60;
const BRANCH_HORIZONTAL_GAP = 400;

const GROUP_PITCH = NODE_WIDTH + PARALLEL_INNER_GAP;

const isMainBranchEdge = (edge: FlowEdge) => edge.condition === 'main';

@injectable()
export class EventTypesLayoutService {
  /** Ширина параллельной группы без отступов */
  groupInnerWidth(nodeCount: number) {
    return nodeCount * GROUP_PITCH - PARALLEL_INNER_GAP;
  }

  /** Вычисляет номер ряда каждого узла */
  private computeRows(
    allNodeIds: string[],
    incomingEdges: Map<string, string[]>,
  ): Map<string, number> {
    const rowByNodeId = new Map<string, number>();
    const rowOf = (id: string, stack: Set<string>): number => {
      if (stack.has(id)) return 0;
      stack.add(id);
      const predecessors = incomingEdges.get(id) ?? [];
      const row = predecessors.length
        ? Math.max(
            ...predecessors.map((parentId) => rowOf(parentId, stack) + 1),
          )
        : 0;
      stack.delete(id);
      rowByNodeId.set(id, row);
      return row;
    };

    allNodeIds.forEach((id) => rowOf(id, new Set()));

    return rowByNodeId;
  }

  private sortTargets(edges: FlowEdge[]): string[] {
    return [...edges]
      .sort(
        (leftEdge, rightEdge) =>
          Number(!isMainBranchEdge(leftEdge)) -
          Number(!isMainBranchEdge(rightEdge)),
      )
      .map((edge) => edge.targetId);
  }

  /** Строит основное дерево обходом в ширину от стартового узла */
  private buildTree(
    startId: string,
    outgoingEdges: Map<string, FlowEdge[]>,
  ): Map<string, string[]> {
    const children = new Map<string, string[]>();
    const visited = new Set<string>([startId]);
    const queue: string[] = [startId];

    while (queue.length) {
      const current = queue.shift();

      if (!current) {
        continue;
      }

      const kids: string[] = [];
      this.sortTargets(outgoingEdges.get(current) ?? []).forEach((target) => {
        if (!visited.has(target)) {
          visited.add(target);
          kids.push(target);
          queue.push(target);
        }
      });
      if (kids.length) children.set(current, kids);
    }

    return children;
  }

  private getGapForChildren(
    id: string,
    nodeTypeById: Map<string, FlowSchema['nodes'][number]['type']>,
  ): number {
    if (nodeTypeById.get(id) !== 'BranchTask') {
      return HORIZONTAL_GAP;
    }

    return BRANCH_HORIZONTAL_GAP;
  }

  /** Рекурсивно вычисляет ширину каждого поддерева */
  private computeSubtreeWidths(
    childrenByNodeId: Map<string, string[]>,
    nodeTypeById: Map<string, FlowSchema['nodes'][number]['type']>,
    ownWidthByNodeId: Map<string, number>,
  ): Map<string, number> {
    const widthByNodeId = new Map<string, number>();

    const subtreeWidth = (id: string): number => {
      const ownWidth = ownWidthByNodeId.get(id) ?? NODE_WIDTH + HORIZONTAL_GAP;
      const kids = childrenByNodeId.get(id) ?? [];
      const kidWidths = kids.map((kid) => subtreeWidth(kid));
      const gap = this.getGapForChildren(id, nodeTypeById);
      const childrenWidth = kidWidths.reduce((sum, kidWidth, index) => {
        const nextGap = index < kidWidths.length - 1 ? gap : 0;

        return sum + kidWidth + nextGap;
      }, 0);
      const width = Math.max(ownWidth, childrenWidth);
      widthByNodeId.set(id, width);
      return width;
    };

    childrenByNodeId.forEach((_, id) => subtreeWidth(id));

    return widthByNodeId;
  }

  /** Назначает горизонтальные центры узлов, размещая детей рядом и центрируя родителя над ними */
  private assignCenterX(
    startId: string,
    childrenByNodeId: Map<string, string[]>,
    widthByNodeId: Map<string, number>,
    nodeTypeById: Map<string, FlowSchema['nodes'][number]['type']>,
    groupIds: Set<string>,
  ): Map<string, number> {
    const centerX = new Map<string, number>();

    const assign = (id: string, center: number): void => {
      const kids = childrenByNodeId.get(id) ?? [];
      if (!kids.length) {
        centerX.set(id, center);
        return;
      }

      const gap = this.getGapForChildren(id, nodeTypeById);
      const kidWidths = kids.map((kid) => widthByNodeId.get(kid) ?? NODE_WIDTH);
      const kidsWidth = kidWidths.reduce(
        (sum, kidWidth, index) =>
          sum + kidWidth + (index < kidWidths.length - 1 ? gap : 0),
        0,
      );

      if (nodeTypeById.get(id) === 'BranchTask' && kids.length === HALF) {
        const [leftKid, rightKid] = kids;
        const [leftKidWidth, rightKidWidth] = kidWidths;
        const totalWidth = leftKidWidth + gap + rightKidWidth;
        const leftCenter = center - totalWidth / HALF + leftKidWidth / HALF;
        const rightCenter = center + totalWidth / HALF - rightKidWidth / HALF;

        assign(leftKid, leftCenter);
        assign(rightKid, rightCenter);
        centerX.set(id, center);
        return;
      }

      const childrenLeft = center - kidsWidth / HALF;

      if (groupIds.has(id)) {
        let cursor = childrenLeft;
        kids.forEach((kid, index) => {
          const kidWidth = kidWidths[index];
          assign(kid, cursor + kidWidth / HALF);
          cursor += kidWidth + gap;
        });
        centerX.set(id, center);
        return;
      }

      let cursor = childrenLeft;
      kids.forEach((kid, index) => {
        const kidWidth = kidWidths[index];
        assign(kid, cursor + kidWidth / HALF);
        cursor += kidWidth;

        if (index < kids.length - 1) {
          cursor += gap;
        }
      });
      centerX.set(id, center);
    };

    const rootWidth = widthByNodeId.get(startId) ?? NODE_WIDTH;
    assign(startId, rootWidth / HALF);

    return centerX;
  }

  /** Вычисляет вертикальную позицию каждого ряда */
  private computeRowY(
    nodesByGroupId: Map<string, FlowNode[]>,
    rowByGroupId: Map<string, number>,
  ): number[] {
    const rows: { isSystem: boolean; isParallel: boolean; height: number }[] =
      [];

    nodesByGroupId.forEach((nodes, groupId) => {
      const rowIndex = rowByGroupId.get(groupId) ?? 0;
      const prev = rows[rowIndex];
      const system = nodes[0].type === 'SystemTask';

      rows[rowIndex] = {
        isSystem: (prev?.isSystem ?? true) && system,
        isParallel: prev?.isParallel || nodes.length > 1,
        height: Math.max(
          prev?.height ?? 0,
          system ? SYSTEM_NODE_HEIGHT : NODE_HEIGHT,
        ),
      };
    });

    let bottom = 0;

    return rows.map(({ isParallel, height }, index) => {
      let gap = VERTICAL_GAP;

      if (!index) gap = 0;
      else if (rows[index - 1]?.isSystem && isParallel)
        gap = SHORT_VERTICAL_GAP;
      const y = bottom + gap + (isParallel ? GROUP_PADDING : 0);

      bottom = y + height + (isParallel ? GROUP_PADDING_BOTTOM : 0);
      return y;
    });
  }

  /** Вычисляет позиции узлов схемы в виде вертикального дерева */
  computeLayout(schema: FlowSchema): Map<string, Point> {
    if (!schema.nodes.length) {
      return new Map<string, Point>();
    }

    const nodesByGroupId = new Map<string, FlowNode[]>();
    const groupIdByNodeId = new Map<string, string>();
    schema.nodes.forEach((node) => {
      const groupId = node.parallelGroupId ?? node.id;
      groupIdByNodeId.set(node.id, groupId);

      const groupNodes = nodesByGroupId.get(groupId) ?? [];
      groupNodes.push(node);
      nodesByGroupId.set(groupId, groupNodes);
    });

    const outgoingEdges = new Map<string, FlowEdge[]>();
    const incomingEdges = new Map<string, string[]>();
    schema.edges.forEach((edge) => {
      const sourceGroupId = groupIdByNodeId.get(edge.sourceId);
      const targetGroupId = groupIdByNodeId.get(edge.targetId);

      if (!sourceGroupId || !targetGroupId) return;
      if (sourceGroupId === targetGroupId) return;

      outgoingEdges.set(sourceGroupId, [
        ...(outgoingEdges.get(sourceGroupId) ?? []),
        { ...edge, sourceId: sourceGroupId, targetId: targetGroupId },
      ]);
      incomingEdges.set(targetGroupId, [
        ...(incomingEdges.get(targetGroupId) ?? []),
        sourceGroupId,
      ]);
    });

    const groupIds = [...nodesByGroupId.keys()];
    const startGroupId =
      groupIds.find((id) => !incomingEdges.get(id)?.length) ?? groupIds[0];

    const nodeTypeByGroupId = new Map(
      groupIds.map((id) => [
        id,
        nodesByGroupId.get(id)?.[0]?.type ?? 'UserTask',
      ]),
    );

    const ownWidthByGroupId = new Map<string, number>();
    nodesByGroupId.forEach((groupNodes, id) => {
      ownWidthByGroupId.set(
        id,
        groupNodes.length > 1
          ? this.groupInnerWidth(groupNodes.length)
          : NODE_WIDTH,
      );
    });

    const rowByGroupId = this.computeRows(groupIds, incomingEdges);
    const childrenByGroupId = this.buildTree(startGroupId, outgoingEdges);
    const widthByGroupId = this.computeSubtreeWidths(
      childrenByGroupId,
      nodeTypeByGroupId,
      ownWidthByGroupId,
    );
    const centerByGroupId = this.assignCenterX(
      startGroupId,
      childrenByGroupId,
      widthByGroupId,
      nodeTypeByGroupId,
      new Set(
        groupIds.filter((id) => (nodesByGroupId.get(id) ?? []).length > 1),
      ),
    );

    const rowY = this.computeRowY(nodesByGroupId, rowByGroupId);

    const positions = new Map<string, Point>();
    nodesByGroupId.forEach((groupNodes, groupId) => {
      const row = rowByGroupId.get(groupId) ?? 0;
      const y = rowY[row] ?? 0;
      const groupCenter = centerByGroupId.get(groupId) ?? 0;

      if (groupNodes.length < HALF) {
        positions.set(groupNodes[0].id, {
          x: groupCenter - NODE_WIDTH / HALF,
          y,
        });
        return;
      }

      const firstNodeCenter =
        groupCenter -
        this.groupInnerWidth(groupNodes.length) / HALF +
        NODE_WIDTH / HALF;

      groupNodes.forEach((node, index) => {
        const centerX = firstNodeCenter + index * GROUP_PITCH;

        positions.set(node.id, {
          x: centerX - NODE_WIDTH / HALF,
          y,
        });
      });
    });

    return positions;
  }
}
